//=================================================================================================
// SCRIPTING
//=================================================================================================

<script>
import MarkdownIt             from 'markdown-it';
import MarkdownItAnchor       from 'markdown-it-anchor';
import MarkdownItContainer    from 'markdown-it-container';
import MarkdownItEmoji        from 'markdown-it-emoji';
import MarkdownItMark         from 'markdown-it-mark';
import MarkdownItMultimdTable from 'markdown-it-multimd-table';
import MarkdownItSup          from 'markdown-it-sup';
import MarkdownItTocDoneRight from 'markdown-it-toc-done-right';
import Vue                    from 'vue';
import Component              from 'vue-class-component';
import { STORAGE_URL }        from '@/assets/json/config.json';
import emoticons              from '@/assets/json/emoticons.json';
import MD_FEATURE             from '@/classes/MdFeature.enum';
import MD_STANDARD            from '@/classes/MdStandard.enum';

const EMOTE_SHORTCUTS   = emoticons;
const PATH_ICONS        = `${STORAGE_URL}/icons`;
const PATH_EMOTES       = `${STORAGE_URL}/emoticons`;
const HIGHTLIGHT_REGEX  = /(.+)\[(.+)\]/;
const FEATURE_STANDARDS = {
	[MD_STANDARD.NONE]    : {},
	[MD_STANDARD.SYSTEM]  : {
		[MD_FEATURE.CONTAINER] : true,
		[MD_FEATURE.EMOTICON]  : true,
		[MD_FEATURE.HIGHLIGHT] : true,
		[MD_FEATURE.IMAGE]     : true,
		[MD_FEATURE.INDEX]     : true,
		[MD_FEATURE.LINK]      : true,
		[MD_FEATURE.SPOILER]   : true,
	},
	[MD_STANDARD.COMMENT] : {
		[MD_FEATURE.EMOTICON]  : true,
		[MD_FEATURE.IMAGE]     : true,
		[MD_FEATURE.SPOILER]   : true,
	},
};

/**
 * TODO
**/
function slugifyString (x, sep) {
	return String(x)
		.replace(/\[.+\]/g, '')        // Remove tags (anything denoted with "[" and "]")
		.replace(/[^a-zA-Z\-\s]/g, '') // Remove special characters
		.trim()
		.toLowerCase()
		.replace(/[\s-]+/g, sep);
}

/**
 * TODO
 * Extends the functionality of the default slugify methods to remove tags:
 * https://github.com/nagaozen/markdown-it-toc-done-right/blob/master/index.js
**/
function slugify (x) {
	return encodeURIComponent(slugifyString(x, '-'));
}

/**
 * TODO
**/
function format (x, htmlencode) {
	const result = HIGHTLIGHT_REGEX.exec(x);

	if (!result) {
		return htmlencode(x);
	} else {
		const head = result[1].trim();
		const tag  = result[2];

		return `${htmlencode(head)} <mark>${htmlencode(tag)}</mark>`
	}
}

/**
 * TODO
**/
function collapseAttributes(attributes) {
	return attributes.map(set => `${set[0]}="${set[1]}"`).join(' ');
}

/**
 * TODO
**/
function containerHeaderOpenRenderer (tokens, index) {
	let token, content;

	// Find header content
	for (let i=index+1, m=tokens.length; i<m; i++) {
		token = tokens[i];

		if (token.type === 'heading_open' && token.tag === 'h5') {
			content = tokens[i+1].children
				.reduce((acc, child) => child.type === 'text' ? acc + child.content : acc, '');
		}

		if (token.type === 'container_header_close') {
			break;
		}
	}

	const image = slugifyString(content, '_');

	return `<div class="header">` + (!!image
		? `<img class="header-image" src="${PATH_ICONS}/${image}.png" />` : '')
		+ `<div class="header-body">`;
}

/**
 * Close "header" & "header-body"
**/
function containerHeaderCloseRenderer () {
	return `</div></div>`;
}

/**
 * TODO
**/
function containerSubHeaderOpenRenderer (tokens, index) {
	let token, content;

	// Find header content
	for (let i=index+1, m=tokens.length; i<m; i++) {
		token = tokens[i];

		console.log(token)

		if (token.type == 'inline') {
			for (const child of token.children) {
				if (!!child.content) {
					content = child.content;
					break;
				}
			}
			break;
		}

		if (token.type === 'container_subheader_close') {
			break;
		}
	}

	const image = slugifyString(content, '_');

	return `<div class="subheader">` + (!!image
		? `<img class="subheader-image" src="${PATH_ICONS}/${image}.png" />` : '')
		+ `<div class="subheader-body">`;
}

/**
 * Close "subheader" & "subheader-body"
**/
function containerSubHeaderCloseRenderer () {
	return `</div></div>`;
}

/**
 * TODO
**/
function emojiRenderer(tokens, index) {
	const emote = tokens[index].markup;

	return `<img class="emote" src="${PATH_EMOTES}/${emote}.png" title="${emote}" />`;
}

/**
 * TODO
**/
function imageRenderer(tokens, index) {
	const { content, attrs } = tokens[index];
	const href               = attrs.find(e => e[0] === 'src');


	return `<div class="image-box">
		<img class="image" ${collapseAttributes(attrs)} />
		${!!href ? `<a class="image-viewer" href="${href[1]}" target="_blank"></a>` : '' }
	</div>
	${!!content ? `<div class="image-flavour">${content}</div>` : '' }`;
}

@Component({
	props : {
		/**
		 * Markdown file contents
		**/
		contents : {
			type     : String,
			required : true,
		},

		/**
		 * Location for image sources.
		**/
		sources  : String,

		/**
		 * Restricts the set of render features based on the supplied standard.
		**/
		standard : {
			type    : String,
			default : MD_STANDARD.NONE,
		},
	},
	watch : {
		'contents' : 'renderHtml',
	},
})
export default class MarkdownRenderer extends Vue {
	// Members
	//--------------------------------------------------------------------------------------------------

	markdownIt = null;
	html       = null;

	// Lifecycle
	//--------------------------------------------------------------------------------------------------

	mounted () {
		this.setupMarkdown();
		this.renderHtml(this.contents); // Attempt initial update
	}

	// Methods
	//--------------------------------------------------------------------------------------------------

	/**
	 * Sets up the markdown-it object with the enabled features.
	**/
	setupMarkdown () {
		const features = FEATURE_STANDARDS[this.standard];

		this.markdownIt = MarkdownIt({
			breaks  : true,
			html    : !!features[MD_FEATURE.HTML],
			image   : !!features[MD_FEATURE.IMAGE],
			link    : !!features[MD_FEATURE.LINK],
			linkify : !!features[MD_FEATURE.LINK],
		});

		// General rendering rules
		this.markdownIt
			.use(MarkdownItSup)
			.use(MarkdownItMultimdTable, {
				headerless : true,
			});

		this.markdownIt.breaks = true;

		// Apply enabled features
		if (features[MD_FEATURE.HIGHLIGHT]) {
			this.markdownIt
				.use(MarkdownItMark);
		}

		if (features[MD_FEATURE.EMOTICON]) {
			this.markdownIt
				.use(MarkdownItEmoji, {
					defs      : Object
						.entries(EMOTE_SHORTCUTS)
						.reduce((defs, [key]) => {
							defs[key] = null;
							return defs;
						}, {}),
					enabled   : Object.keys(EMOTE_SHORTCUTS),
					shortcuts : EMOTE_SHORTCUTS,
				});

			this.markdownIt.renderer.rules['emoji'] = emojiRenderer;
		}

		if (features[MD_FEATURE.CONTAINER]) {
			this.markdownIt
				.use(MarkdownItContainer, 'body')
				.use(MarkdownItContainer, 'banner')
				.use(MarkdownItContainer, 'header')
				.use(MarkdownItContainer, 'subheader');

			this.markdownIt.renderer.rules['container_header_open']     = containerHeaderOpenRenderer;
			this.markdownIt.renderer.rules['container_header_close']    = containerHeaderCloseRenderer;
			this.markdownIt.renderer.rules['container_subheader_open']  = containerSubHeaderOpenRenderer;
			this.markdownIt.renderer.rules['container_subheader_close'] = containerSubHeaderCloseRenderer;
		}

		if (features[MD_FEATURE.SPOILER]) {
			this.markdownIt
				.use(MarkdownItContainer, 'spoiler');
		}

		if (features[MD_FEATURE.INDEX]) {
			this.markdownIt
				.use(MarkdownItAnchor, {
					slugify,
				})
				.use(MarkdownItTocDoneRight, {
					format,
					slugify,
					listType : 'ul',
				});
		}

		if (features[MD_FEATURE.IMAGE]) {
			this.markdownIt
				.renderer.rules['image'] = imageRenderer;
		}
	}

	/**
	 * TODO
	**/
	renderHtml (contents) {
		if (!!contents) {
			this.html = this.markdownIt.render(contents);

			Vue.nextTick().then(this.updateCollapsers);
		} else {
			this.html = null;
		}
	}

	/**
	 * TODO
	**/
	updateCollapsers () {
		const collapsers = document.getElementsByClassName('spoiler');

		for (const collapser of collapsers) {
			collapser.addEventListener('click', this.revealItem);
		}
	}

	/**
	 * TODO
	**/
	revealItem (event) {
		const element = event.target;

		element.classList.add('revealed');
	}
}
</script>

//=================================================================================================
// TEMPLATING
//=================================================================================================

<template>
	<div ref="contents" v-html="html" class="rendered" />
</template>

//=================================================================================================
// STYLING
//=================================================================================================
// NOTE: These styles are not scoped

<style lang="scss">
/* Unscoped styling, as the rendering is external */
@import '../assets/scss/colour.scss';
@import '../assets/scss/constant.scss';

.rendered {
	display                     : flex;

	p {
		text-align                  : justify;
	}

	blockquote {
		position                    : relative;
		margin                      : 2px 0 10px;
		padding                     : 5px 0 5px 10px;
		text-indent                 : 25px;
		color                       : $c--highlight;

		&::before {
			content                     : '';
			position                    : absolute;
			top                         : 0;
			left                        : 0;
			bottom                      : 0;
			width                       : 5px;
			border                      : 2px solid $c--secondary;
			border-right                : none;
		}

		&::after {
			z-index                     : -1;
			content                     : '';
			position                    : absolute;
			top                         : 0;
			left                        : 2px;
			bottom                      : 0;
			right                       : 0;
			background-color            : $c--secondary;
			mask-image                  : url('../assets/images/blockquote outline.svg');
			mask-repeat                 : no-repeat;
			mask-position               : 5px top;
		}
	}

	table {
		position                    : relative;
		padding                     : 0 0.25em;
		margin-top                  : 0.2em;

		&::before,
		&::after {
			content                     : '';
			position                    : absolute;
			left                        : 0;
			right                       : 0;
			height                      : 5px;
			border                      : 2px solid $c--secondary;
		}

		&::before {
			top                         : 0;
			border-bottom               : none;
		}

		&::after {
			bottom                      : 0;
			border-top                  : none;
		}

		& td:nth-child(2) {
			font-weight                 : bold;
			color                       : $c--highlight;
			padding                     : 0 0.5em;
		}
	}

	// TEMP: Should filter out marks instead (and remove square brackets in the changelogs)
	mark {
		&::before {
			content                     : '';
		}

		&::after {
			content                     : '';
		}
	}

	.emote {
		display                     : inline-block;
		width                       : 15px;
		width                       : 15px;
		margin-bottom               : -1px;
	}

	// Image viewer
	//--------------------------------------------------------------------------------------------------
	// TODO: Nest scss for visibility

	img.image {
		max-width                   : 100%;
		border                      : 2px dashed $c--text;
		box-sizing                  : border-box;
	}

	.image-box {
		position                    : relative;
	}

	.image-flavour {
		margin-top                  : 0.8em;
		text-align                  : center;
		font-size                   : 80%;
		color                       : $c--disabled;
	}

	.image-viewer {
		position                    : absolute;
		top                         : 0;
		left                        : 0;
		bottom                      : 0;
		right                       : 0;
		border                      : solid $c--highlight;
		box-sizing                  : border-box;
		opacity                     : 0.0;
		background                  : url('../assets/images/zoom.png') no-repeat center;
		background-size             : 10%;
		transition                  : opacity $x--transition-time;

		&:hover {
			opacity                     : 1.0;
		}
	}

	// Banner definition below
	.banner .image-viewer {
		border                      : none;
	}

	// Header container
	//--------------------------------------------------------------------------------------------------

	.header {
		display                     : flex;
		margin-top                  : 25px; // Match h5
		margin-bottom               : 5px;  // Match h5

		.header-image {
			border                      : 2px solid $c--highlight;
			width                       : 40px;
			height                      : 40px;
			display                     : inline;
			vertical-align              : middle;
			margin-right                : 10px;
		}

		.header-body {
			display                     : flex;
			flex-direction              : column;
			justify-content             : center;

			& > h5 {
				margin                      : 0;
			}

			& > p {
				margin-top                  : 0.2em;
				margin-bottom               : 0;
				font-size                   : 80%;
				font-style                  : italic;
			}
		}
	}

	// Subheader container
	//--------------------------------------------------------------------------------------------------

	.subheader {
		display                     : flex;
		align-items                 : center;

		.subheader-image {
			display                     : block;
			border                      : 2px solid $c--highlight;
			margin                      : 2px 0;
			width                       : 26px;
			height                      : 26px;
			display                     : inline;
			vertical-align              : middle;
			margin-right                : 10px;
		}

		.subheader-body > p {
			font-size                   : 80%;
			font-style                  : italic;

			& > em {
				color                       : $c--highlight;
				font-size                   : 125%; // Revert sizing changed above
				font-style                  : normal;
			}
		}
	}


	// Table of contents
	//--------------------------------------------------------------------------------------------------

	.table-of-contents {
		overflow-x                  : hidden;
		padding-right               : 20px;
		font-size                   : 90%;

		& a {
			text-decoration             : none;

			&:hover {
				color                       : $c--highlight;
				text-decoration             : underline;
			}
		}

		& ul {
			margin-bottom               : 10px;
			text-indent                 : -0.75em;
			padding-left                : 1.5em;
			border-left                 : 1px solid $c--text;
			transition                  : border-color $x--transition-time;

			li::before {
				content                     : none;
			}

			&:hover {
				border-color                : white;
			}
		}

		& > ul {
			margin-left                 : -0.75em;
			border-left                 : none;
		}

		@media only screen and (max-width: 1199px) {
			visibility                  : hidden;
			z-index                     : 2; // TEMP
			position                    : fixed;
			top                         : 0;
			bottom                      : 0;
			margin-top                  : 0;
			padding-top                 : 80px;
			border-right                : 2px solid white;
			background-color            : $c--background;
			overflow-y                  : scroll;
		}

		@media only screen and (min-width: 1200px) {
			width                       : 25%;
		}
	}

	&.table-of-content-visible .table-of-contents {
		visibility                  : visible;
	}

	// Body container (works in conjuction with ToC)
	//--------------------------------------------------------------------------------------------------

	.body {
		position                    : relative;
		overflow                    : hidden;
		flex                        : 1 1 0;

		p {
			margin                      : 0;
			padding                     : 0;
		}
	}

	// Banner container
	//--------------------------------------------------------------------------------------------------

	.banner {
		margin-bottom               : 50px;

		img {
			z-index                     : -1;
			position                    : relative;
			width                       : 100%;
			margin-top                  : calc(-7.5% + -1.5em);
			border                      : 2px solid white;
			box-sizing                  : border-box;
			mask-image                  : linear-gradient(rgba(0,0,0,0) 10%, white 35%, white);
		}

		p {
			color                       : $c--highlight;
			margin                      : 1.5em;
		}

		p:last-of-type {
			margin                      : 0;
		}
	}

	// Spoiler container
	//--------------------------------------------------------------------------------------------------

	.spoiler:not(.revealed) {
		$forced-height              : calc(1.4rem + 4px);
		//---
		margin                      : 0.5em 0 0.5em 1.5em;
		height                      : $forced-height;
		max-height                  : $forced-height;

		& > * {
			display: none;
		}

		&::before {
			content                     : 'Reveal Details';
			visibility                  : visible;
			padding                     : 0 10px;
			border                      : 2px dashed $c--text;
			transition                  : border-color, color;
			transition-duration         : $x--transition-time;
			cursor                      : pointer;
			white-space                 : nowrap;
		}

		&:hover::before {
			color                       : $c--highlight;
			border-color                : $c--highlight;
			border-style                : solid;
		}
	}

	.spoiler.revealed {
		& *:last-child {
			margin-bottom               : 0;
		}
	}
}
</style>
